import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

// Hooks
import useErrorTranslation from '@/utils/hooks/language/useErrorTranslation';
import useTranslation from '@/utils/hooks/translation/useTranslation';

// Utils
import { put } from '@/utils/client';

// Endpoints
import { UPDATE_PASSWORD } from '@/lib/routes/apiEndpoints';

// Components
import Button from '@/components/base/Button';
import Icon from '@/components/base/Icon';
import CompoundBanner from '@/components/base/InfoBanner/CompoundBanner';
import Input from '@/components/base/Input';
import Label from '@/components/base/Label';
import PasswordField from '@/components/pages/signup/PasswordField';

const keystrokes = ['enter', ' ', 'space'];

const SecurityForm = () => {
    const { t } = useTranslation('settings');
    const { t_error } = useErrorTranslation();

    const [formMessages, setFormMessages] = useState({
        successMessage: false,
        errorMessage: null
    });

    const [currentPasswordDisplay, setCurrentPasswordDisplay] = useState(false);

    const formMethods = useForm();
    const {
        register,
        setError,
        handleSubmit,
        formState: { errors, isSubmitting }
    } = formMethods;

    const onSubmit = async ({ current_password, password, password_confirmation }) => {
        setFormMessages((prevState) => ({
            ...prevState,
            successMessage: false,
            errorMessage: null
        }));

        try {
            await put(UPDATE_PASSWORD, { current_password, password, password_confirmation });
            setFormMessages((prevState) => ({
                ...prevState,
                successMessage: true
            }));
        } catch (e) {
            if (e.status === 422) {
                const errorsFields = e.response?.data?.errors;
                if (errorsFields) {
                    Object.keys(errorsFields).map((field) => {
                        setError(field, {
                            type: 'manual',
                            message: t_error('errors:error_' + field)
                        });
                    });
                } else {
                    setFormMessages((prevState) => ({
                        ...prevState,
                        errorMessage: t('errors:error_occured_submit_data')
                    }));
                }
            } else {
                setFormMessages((prevState) => ({
                    ...prevState,
                    errorMessage: t('errors:error_occured_submit_data')
                }));
            }
        }
    };

    return (
        <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)} id="settings-platform-form">
                {formMessages.successMessage && (
                    <CompoundBanner
                        type="success"
                        className="mb-8 w-full"
                        data-testid="banner-success">
                        <CompoundBanner.Icon icon="check" />
                        <CompoundBanner.Content
                            title={t('commons:success')}
                            message={t('settings_banner_success')}
                        />
                        <CompoundBanner.Close
                            onClose={() =>
                                setFormMessages((prevState) => ({
                                    ...prevState,
                                    successMessage: false
                                }))
                            }
                        />
                    </CompoundBanner>
                )}
                {formMessages.errorMessage !== null && (
                    <CompoundBanner className="mb-8 w-full" data-testid="banner-error">
                        <CompoundBanner.Icon />
                        <CompoundBanner.Content
                            title={t('commons:error')}
                            message={formMessages.errorMessage}
                        />
                        <CompoundBanner.Close
                            onClose={() =>
                                setFormMessages((prevState) => ({
                                    ...prevState,
                                    errorMessage: null
                                }))
                            }
                        />
                    </CompoundBanner>
                )}
                <p className="mb-7 text-sm leading-5">
                    {t('security_page.notice.part_1')}
                    <br />
                    {t('security_page.notice.part_2')}
                </p>
                <div className="mb-4">
                    <Label required htmlFor="current_password">
                        {t('commons:old_password')}
                    </Label>
                    <Input
                        id="current_password"
                        type={currentPasswordDisplay ? 'text' : 'password'}
                        placeholder={t('commons:enter_old_password')}
                        error={errors?.current_password?.message}
                        {...register('current_password', {
                            required: t('errors:error_current_password_required')
                        })}
                        afterInput={
                            <Icon
                                id="button-display-password"
                                tabIndex="0"
                                className="rounded-xs cursor-pointer align-middle transition-colors hover:text-icoBlue focus:p-0.5 focus:text-icoBlue focus:outline-none focus:ring-2 focus:ring-icoBlue-200"
                                onClick={() => setCurrentPasswordDisplay(!currentPasswordDisplay)}
                                //Add keyboard event for accessibility
                                onKeyDown={(e) => {
                                    if (keystrokes.includes(e.key.toLowerCase()))
                                        return setCurrentPasswordDisplay(!currentPasswordDisplay);
                                }}
                                icon={currentPasswordDisplay ? 'visible' : 'invisible'}
                            />
                        }
                    />
                </div>
                <PasswordField isPasswordUpdate={true} />
                <div className="mt-8 flex justify-end">
                    <Button type="submit" data-testid="submitButton" isLoading={isSubmitting}>
                        {t('security_page.change_password')}
                    </Button>
                </div>
            </form>
        </FormProvider>
    );
};

export default SecurityForm;
